import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AppService } from "src/app/core/services/app.service";
import { FocuslinkService } from "src/app/core/services/focuslink.service";
import { AppMessageSeverity } from "src/app/shared/models/enums/AppMessageSeverity.enum";
import { FocusPayApplication, MerchantContact } from "src/app/shared/models/FocusPay/FocusPayApplication.item";
import { v4 as uuidv4 } from "uuid";
import { ApplicationBankingInformationComponent } from "./application-banking-information/application-banking-information.component";
import { ApplicationBusinessInfoComponent } from "./application-business-info/application-business-info.component";
import { ApplicationLegalInformationComponent } from "./application-legal-information/application-legal-information.component";
import { ApplicationMerchantContactComponent } from "./application-merchant-contact/application-merchant-contact.component";
import { ApplicationRestaurantInformationComponent } from "./application-restaurant-information/application-restaurant-information.component";

@Component({
	selector: "app-application",
	templateUrl: "./application.component.html",
	styleUrls: ["../focuspay.style.scss"],
})
export class ApplicationComponent implements OnInit {
	@ViewChild("banking") appBanking: ApplicationBankingInformationComponent;
	@ViewChild("business") appBusiness: ApplicationBusinessInfoComponent;
	@ViewChild("legal") appLegal: ApplicationLegalInformationComponent;
	@ViewChild("owner") appOwner: ApplicationMerchantContactComponent;
	@ViewChild("restaurant") appRestaurant: ApplicationRestaurantInformationComponent;

	allowSave: boolean = false;
	allowSubmit: boolean = false;

	application: FocusPayApplication = new FocusPayApplication();
	@Input() applicationId: string;
	applicationSections = [
		{
			section: "banking",
			complete: false,
		},
		{
			section: "legal",
			complete: false,
		},
		{
			section: "restaurant",
			complete: false,
		},
		{
			section: "business",
			complete: false,
		},
		{
			section: "owner",
			complete: false,
		},
	];

	applicationSubmitButtonLabel: string = "Submit Application!";
	applicationSubmittedSuccessfully: boolean = false;

	beta: boolean = true;

	comingSoon: boolean = true;

	displayModal: string;

	@Input() readOnly: boolean;

	validationErrorMessage: {
		section: string;
		message: string;
	} = undefined;
	volumeInputMask = ["$", /\d/, /\d/, /\d/, /\d/, /\d/];

	constructor(public app: AppService, private route: ActivatedRoute, private focuslink: FocuslinkService) {}

	ngOnInit(): void {
		this.app.titleService.setTitle(`Apply for FocusPay!`);
		this.application = new FocusPayApplication({
			merchantContacts: [
				new MerchantContact({
					isPoc: true,
				}),
			],
		});

		const testing: string = this.route.snapshot.queryParamMap.get("test");
		const applicationId = this.applicationId || this.route.snapshot.queryParamMap.get("id");
		if (applicationId || testing) {
			this.comingSoon = false;
			applicationId && this.getApplication(applicationId);
		}
		this.evaluateFormForSave();
	}

	evaluateLoadedApplication() {
		this.appBanking.sectionStatus.subscribe((status) => {
			const section = this.applicationSections.find((i) => i.section === status.section);
			section.complete = status.complete;
			this.evaluateSectionComplete(status);
		});
		this.appBusiness.sectionStatus.subscribe((status) => {
			const section = this.applicationSections.find((i) => i.section === status.section);
			section.complete = status.complete;
			this.evaluateSectionComplete(status);
		});
		this.appLegal.sectionStatus.subscribe((status) => {
			const section = this.applicationSections.find((i) => i.section === status.section);
			section.complete = status.complete;
			this.evaluateSectionComplete(status);
		});
		this.appOwner.sectionStatus.subscribe((status) => {
			const section = this.applicationSections.find((i) => i.section === status.section);
			section.complete = status.complete;
			this.evaluateSectionComplete(status);
		});
		this.appRestaurant.sectionStatus.subscribe((status) => {
			const section = this.applicationSections.find((i) => i.section === status.section);
			section.complete = status.complete;
			this.evaluateSectionComplete(status);
		});
	}

	evaluateFormForSave() {
		let allowSubmit = false;
		this.allowSave = false;
		if (!this.application.partnerApplicationStatus) {
			//evaluate for save
			for (const [key, value] of Object.entries(this.application)) {
				if (value && typeof value !== "object" && key !== "productSold") {
					//console.log(`allow save from: ${key} (${typeof value}) === ${value}`);
					//console.log(`type = ${typeof value}`);
					this.allowSave = true;
					break;
				}
				if (typeof value === "object") {
					for (const [subKey, subValue] of Object.entries(value)) {
						if (subValue && subKey !== "country") {
							//		console.log(`allow save from: ${subKey} === ${subValue}`);
							//		console.log(`type = ${typeof value}`);
							this.allowSave = true;
							break;
						}
					}
				}
			}
		}
	}

	evaluateSectionComplete(sectionState: { section: string; complete: boolean }) {
		const appSection = this.applicationSections.find((i) => i.section === sectionState.section);
		appSection.complete = sectionState.complete;
		if (!this.applicationSections.some((i) => i.complete === false)) {
			this.allowSubmit = true;
		} else {
			this.applicationSections.forEach((section) => {
				if (!section.complete) {
					//console.log(`${section.section} is incomplete`);
				}
			});
			this.allowSubmit = false;
		}
	}

	async getApplication(applicationId: string) {
		const resp = await this.focuslink.focuspayApplicationGetOne(applicationId);
		this.application = new FocusPayApplication(resp);
		if (this.application.sk) {
			this.evaluateLoadedApplication();
			if (this.application.accountNameDba) {
				this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: "", detail: `Application for ${this.application.accountNameDba} restored` });
			}
		} else {
			this.application.sk = uuidv4();
		}
		if (this.application.partnerApplicationStatus) {
			this.applicationSubmitButtonLabel = "Application already submitted.";
		}
	}

	inputAction(event) {
		this.evaluateFormForSave();
	}

	async saveApplication() {
		const resp = await this.focuslink.focuspayApplicationPost(this.application);
		this.application.sk = resp.sk;
		this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: "", detail: `Application saved! Thanks!` });
	}

	submitApplicationPrompt() {
		this.app.confirmation.confirm({
			message: `Are you ready to submit your application?`,
			accept: async () => {
				this.submitApplication();
			},
			reject: async () => {
				this.app.confirmation.close();
			},
		});
	}

	async submitApplication() {
		this.applicationSubmittedSuccessfully = true;
		let resp = await this.focuslink.focuspayApplicationPost(this.application);
		resp = await this.focuslink.focuspayApplicationSubmit(resp.sk);
	}

	submitCriteriaMet() {}
}
